import { HTTPError } from 'ky';
import { apiClient } from './api-client';
import type {
  ApprovalBox,
  ApprovalLine,
  ApprovalProgram,
  ApprovalProgramSetting,
  ApprovalRequestDetail,
  ApprovalCounts,
  ApprovalListFilter,
  ApprovalRequestSummary,
  ApprovalSummary,
  ApprovalView,
  DealerPriceCreateRequest,
  Delegation,
  DelegationSaveRequest,
  JobTitle,
  MyProfile,
  PendingApprover,
  ProgramUseSaveRequest,
  RuleSetDetail,
  RuleSetSaveRequest,
  RuleSetSummary,
  StorePriceCreateRequest,
  StorePriceCurrent,
  DealerPriceCurrent,
} from '@/types/approval';

/** PagedModel 응답 (PetrohqApiResponse<PagedModel<T>>) */
interface Paged<T> {
  content: T[];
  page?: { size: number; number: number; totalElements: number; totalPages: number };
}

/** 메뉴 키 1개 ↔ 경로 프리픽스 1개 (api V63 menu_api_path 시드) */
export const APPROVAL_MENU_KEY: Record<string, { box: ApprovalBox; program: ApprovalProgram; title: string }> = {
  b210: { box: 'requests', program: 'store-price', title: '판매단가 승인요청' },
  b220: { box: 'requests', program: 'dealer-price', title: '거래처 계약단가 승인요청' },
  a330: { box: 'inbox', program: 'store-price', title: '판매단가 승인' },
  a340: { box: 'inbox', program: 'dealer-price', title: '거래처 계약단가 승인' },
};

/** 결재 SPA 진입에 필요한 메뉴 키 (V63 시드). 하나라도 있으면 창을 연다 */
export const APPROVAL_ENTRY_MENU_KEYS = [...Object.keys(APPROVAL_MENU_KEY), 'approval-admin'];

const base = (box: ApprovalBox, program: ApprovalProgram) => `approvals/${box}/${program}`;

/**
 * 빈 값은 아예 빼서 "전체" 로 조회한다. 사업장은 부서를 고르면 하위 사업장 여러 개가 되므로
 * 콤마 결합이 아니라 같은 키를 반복해 보낸다 (서버는 List<String> 으로 받는다)
 */
function filterParams(filter?: ApprovalListFilter): URLSearchParams {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filter ?? {})) {
    if (Array.isArray(value)) {
      value.filter(Boolean).forEach(v => params.append(key, v));
    } else if (value) {
      params.set(key, String(value));
    }
  }
  return params;
}

export const approvalClient = {
  async list(
    box: ApprovalBox,
    program: ApprovalProgram,
    params: { view?: ApprovalView; filter?: ApprovalListFilter; page?: number; size?: number } = {}
  ): Promise<Paged<ApprovalRequestSummary>> {
    const res = await apiClient
      .get(base(box, program), {
        searchParams: (() => {
          const search = filterParams(params.filter);
          if (params.view) search.set('view', params.view);
          search.set('page', String(params.page ?? 0));
          search.set('size', String(params.size ?? 50));
          return search;
        })(),
      })
      .json<{ result?: Paged<ApprovalRequestSummary> }>();
    return res.result ?? { content: [] };
  },

  /** 목록 하단 집계 6종. 조회구분만 빼고 같은 조회조건을 보낸다 */
  async counts(box: ApprovalBox, program: ApprovalProgram, filter?: ApprovalListFilter): Promise<ApprovalCounts> {
    const res = await apiClient
      .get(`${base(box, program)}/counts`, { searchParams: filterParams(filter) })
      .json<{ result: ApprovalCounts }>();
    return res.result;
  },

  /** 로그인 사용자의 소속·직책. 조회조건이 사업장·부서 입력을 고정할지 판단한다 */
  async profile(): Promise<MyProfile> {
    const res = await apiClient.get('me/profile').json<{ result: MyProfile }>();
    return res.result;
  },

  /** 대시보드 위젯 건수. `/me/**` 라 메뉴 권한 없이도 200 이고, 본인 건만 센다 */
  async summary(): Promise<ApprovalSummary> {
    const res = await apiClient.get('me/approvals/summary').json<{ result: ApprovalSummary }>();
    return res.result;
  },

  async detail(box: ApprovalBox, program: ApprovalProgram, id: number): Promise<ApprovalRequestDetail> {
    const res = await apiClient.get(`${base(box, program)}/${id}`).json<{ result: ApprovalRequestDetail }>();
    return res.result;
  },

  /**
   * 알림 딥링크(`/approvals/{id}`)에는 프로그램이 없어 사용자가 가진 메뉴 키 순서대로 조회한다.
   * ponytail: 보통 1회 호출로 끝난다. 호출이 거슬리면 api 에 프로그램 무관 상세 엔드포인트를 열고 menu_api_path 시드를 추가할 것
   */
  async findDetail(
    id: number,
    candidates: { box: ApprovalBox; program: ApprovalProgram }[]
  ): Promise<{ detail: ApprovalRequestDetail; box: ApprovalBox; program: ApprovalProgram }> {
    let lastError: unknown = new Error('접근 가능한 결재 화면이 없습니다');
    for (const c of candidates) {
      try {
        return { detail: await approvalClient.detail(c.box, c.program, id), box: c.box, program: c.program };
      } catch (error) {
        const status = error instanceof HTTPError ? error.response.status : undefined;
        if (status !== 403 && status !== 404) throw error;
        lastError = error;
      }
    }
    throw lastError;
  },

  /** 상신 전 결재선. 상신과 같은 본문·검증이라 필수값이 다 있어야 200, 규정 미사용이면 빈 배열 */
  async previewLine(
    program: ApprovalProgram,
    body: StorePriceCreateRequest | DealerPriceCreateRequest
  ): Promise<ApprovalLine[]> {
    const res = await apiClient
      .post(`approvals/requests/${program}/line-preview`, { json: body })
      .json<{ result?: ApprovalLine[] }>();
    return res.result ?? [];
  },

  /** 그 매장·유종·적용구분에 지금 적용돼 있는 단가. 첫 요청이면 null 이다 */
  async currentStorePrice(orgCd: string, productCode: string, applyType: string): Promise<StorePriceCurrent | null> {
    const res = await apiClient
      .get('approvals/requests/store-price/current', { searchParams: { orgCd, productCode, applyType } })
      .json<{ result?: StorePriceCurrent }>();
    return res.result ?? null;
  },

  /** 그 매장·거래처·유종·판매구분에 오늘 유효한 계약단가(geo_dg). 신규 계약이면 null 이다 */
  async currentDealerPrice(
    orgCd: string,
    dealerCode: string,
    productCode: string,
    oilSaleType: string
  ): Promise<DealerPriceCurrent | null> {
    const res = await apiClient
      .get('approvals/requests/dealer-price/current', { searchParams: { orgCd, dealerCode, productCode, oilSaleType } })
      .json<{ result?: DealerPriceCurrent }>();
    return res.result ?? null;
  },

  async submitStorePrice(body: StorePriceCreateRequest): Promise<ApprovalRequestDetail> {
    const res = await apiClient
      .post('approvals/requests/store-price', { json: body })
      .json<{ result: ApprovalRequestDetail }>();
    return res.result;
  },

  async submitDealerPrice(body: DealerPriceCreateRequest): Promise<ApprovalRequestDetail> {
    const res = await apiClient
      .post('approvals/requests/dealer-price', { json: body })
      .json<{ result: ApprovalRequestDetail }>();
    return res.result;
  },

  /** 기안자 액션: cancel(자진취소) / cancel-request(승인 후 취소요청) */
  async drafterAction(
    program: ApprovalProgram,
    id: number,
    action: 'cancel' | 'cancel-request',
    comment?: string
  ): Promise<ApprovalRequestDetail> {
    const res = await apiClient
      .post(`approvals/requests/${program}/${id}/${action}`, { json: { comment: comment ?? null } })
      .json<{ result: ApprovalRequestDetail }>();
    return res.result;
  },

  /** 결재자 액션: approve / reject / cancel-approve / cancel-reject */
  async approverAction(
    program: ApprovalProgram,
    id: number,
    action: 'approve' | 'reject' | 'cancel-approve' | 'cancel-reject',
    comment?: string
  ): Promise<ApprovalRequestDetail> {
    const res = await apiClient
      .post(`approvals/inbox/${program}/${id}/${action}`, { json: { comment: comment ?? null } })
      .json<{ result: ApprovalRequestDetail }>();
    return res.result;
  },
};

export const approvalAdminClient = {
  async programs(): Promise<ApprovalProgramSetting[]> {
    const res = await apiClient.get('approvals/admin/programs').json<{ result?: ApprovalProgramSetting[] }>();
    return res.result ?? [];
  },
  async saveProgramUse(programId: string, body: ProgramUseSaveRequest): Promise<ApprovalProgramSetting> {
    const res = await apiClient
      .put(`approvals/admin/programs/${programId}/use`, { json: body })
      .json<{ result: ApprovalProgramSetting }>();
    return res.result;
  },
  async ruleSets(programId?: string): Promise<RuleSetSummary[]> {
    const res = await apiClient
      .get('approvals/admin/rule-sets', { searchParams: programId ? { programId } : undefined })
      .json<{ result?: RuleSetSummary[] }>();
    return res.result ?? [];
  },
  async ruleSet(id: number): Promise<RuleSetDetail> {
    const res = await apiClient.get(`approvals/admin/rule-sets/${id}`).json<{ result: RuleSetDetail }>();
    return res.result;
  },
  async createRuleSet(body: RuleSetSaveRequest): Promise<RuleSetDetail> {
    const res = await apiClient.post('approvals/admin/rule-sets', { json: body }).json<{ result: RuleSetDetail }>();
    return res.result;
  },
  async updateRuleSet(id: number, body: RuleSetSaveRequest): Promise<RuleSetDetail> {
    const res = await apiClient
      .put(`approvals/admin/rule-sets/${id}`, { json: body })
      .json<{ result: RuleSetDetail }>();
    return res.result;
  },
  async delegations(originalApproverId?: number): Promise<Delegation[]> {
    const res = await apiClient
      .get('approvals/admin/delegations', {
        searchParams: originalApproverId ? { originalApproverId } : undefined,
      })
      .json<{ result?: Delegation[] }>();
    return res.result ?? [];
  },
  async createDelegation(body: DelegationSaveRequest): Promise<Delegation> {
    const res = await apiClient.post('approvals/admin/delegations', { json: body }).json<{ result: Delegation }>();
    return res.result;
  },
  async deleteDelegation(id: number): Promise<void> {
    await apiClient.delete(`approvals/admin/delegations/${id}`);
  },
  async jobTitles(includeInactive = false): Promise<JobTitle[]> {
    const res = await apiClient
      .get('approvals/admin/job-titles', { searchParams: { includeInactive } })
      .json<{ result?: JobTitle[] }>();
    return res.result ?? [];
  },
  async saveJobTitle(body: JobTitle): Promise<JobTitle> {
    const res = await apiClient.put('approvals/admin/job-titles', { json: body }).json<{ result: JobTitle }>();
    return res.result;
  },
  async pendingApprovers(): Promise<PendingApprover[]> {
    const res = await apiClient.get('approvals/admin/pending-approvers').json<{ result?: PendingApprover[] }>();
    return res.result ?? [];
  },
};
